import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { Progress } from 'antd';
import { countFormatter } from 'common/helpers/formatter.helper';
import { DEFAULT_VALUE_0, PERCENT_100, PROGRESS_STEP_WIDTH_PX, PROGRESS_STEPS_DEFAULT_VALUE } from 'common/config';
import { getRequestProgressSteps, getRequestStatus } from 'entities/Request/Request.helper';
import { IRequest } from 'entities/Request/Request.models';

interface IComponentProps {
  request: IRequest;
}

export const RequestProgressBar: FC<IComponentProps> = ({ request }) => {
  const [steps, setSteps] = useState<number>(PROGRESS_STEPS_DEFAULT_VALUE);
  const ref = useRef<HTMLDivElement | null>(null);

  const { isProcessingRequest } = getRequestStatus(request?.status);
  const progressSteps = useMemo(() => getRequestProgressSteps(request, steps), [request, steps]);

  useEffect(() => {
    const resizeObserver = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setSteps(Math.round(entry.contentRect.width / PROGRESS_STEP_WIDTH_PX));
      }
    });

    if (ref.current) {
      resizeObserver.observe(ref.current);
    }

    return () => {
      resizeObserver.disconnect();
    };
  }, []);

  if (!isProcessingRequest) {
    return null;
  }

  return (
    <div ref={ref} className="request-progress-bar">
      <div className="request-progress-bar__container">
        <div className="request-progress-bar__progress">
          <Progress
            type="line"
            steps={steps}
            percent={PERCENT_100}
            showInfo={false}
            strokeColor={progressSteps?.flatMap((item) => {
              return item.steps > DEFAULT_VALUE_0 ? Array(item.steps).fill(item.color) : [];
            })}
            size="small"
          />
        </div>
      </div>

      <div className="request-progress-bar__labels">
        {progressSteps.map((item) => {
          if (item.steps <= DEFAULT_VALUE_0) {
            return null;
          }

          return (
            <div
              key={item.key}
              className={`request-progress-bar__labels-item ${item.key}`}
              style={{ flexBasis: `${(item.steps / steps) * PERCENT_100}%` }}
            >
              <span className="text-tag-accent">{item.label}</span>
              <div className="text-tag request-progress-bar__labels-item-count">{countFormatter(item.count)}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
